import { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { gerarAutomaticaBatch, previewAutomatica } from './api';
import { parseColabNotaFiscalCodigo } from './types';
import type {
  NotaFiscalColabPendenteItem,
  NotaMaeOption,
  PreviewAutomaticaInput,
  PreviewAutomaticaLinha,
} from './types';

function formatBRL(v: number): string {
  return v.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

export interface PreviewAutomaticaDialogProps {
  open: boolean;
  companyId: number;
  linhaSelecionada: NotaFiscalColabPendenteItem | null;
  modelo: string;
  serie: string;
  notaMae: NotaMaeOption | null;
  onClose: () => void;
  onEmitido: () => void;
}

export function PreviewAutomaticaDialog(props: PreviewAutomaticaDialogProps): JSX.Element {
  const { open, companyId, linhaSelecionada, modelo, serie, notaMae, onClose, onEmitido } = props;
  const { t } = useTranslation();
  const toast = useToast();

  const previewInput: PreviewAutomaticaInput | null = useMemo(() => {
    if (!linhaSelecionada || !serie) return null;
    return {
      modelo,
      serie,
      colaboradorCodigo: linhaSelecionada.colaboradorCodigo,
      colaboradorNotaFiscalCodigo: parseColabNotaFiscalCodigo(linhaSelecionada.id),
      notaMaeEmpresaCodigo: notaMae ? Number(notaMae.id.split('-')[0] ?? notaMae.id) : null,
      notaMaeEmpresaNotaFiscalCodigo: notaMae ? Number(notaMae.id) : null,
    };
  }, [linhaSelecionada, modelo, serie, notaMae]);

  const previewQuery = useQuery({
    queryKey: ['nf-empresa-gerar', 'preview-automatica', companyId, previewInput],
    queryFn: () => previewAutomatica(companyId, previewInput as PreviewAutomaticaInput),
    enabled: open && previewInput !== null,
    staleTime: 0,
  });

  // Rowid client-side (backend não emite id — chave estável via índice + RC).
  const rows = useMemo(
    () =>
      (previewQuery.data ?? []).map((l, idx) => ({
        ...l,
        rowId: `${idx}-${l.requisicaoCompraCodigo ?? 'residuo'}`,
      })),
    [previewQuery.data],
  );

  const gerarMut = useMutation({
    mutationFn: () => {
      if (!previewInput) throw new Error('previewInput ausente');
      const requisicoes = rows
        .filter((r) => r.emissaoOK && r.requisicaoCompraCodigo !== null)
        .map((r) => ({
          requisicaoCompraCodigo: r.requisicaoCompraCodigo as number,
          valor: r.valor,
        }));
      return gerarAutomaticaBatch(companyId, { ...previewInput, requisicoes });
    },
    onSuccess: (r) => {
      toast.success(
        t('features.empresa.gerarNfEmpresa.preview.toasts.emitidas', { count: r.emitidas }),
      );
      onEmitido();
    },
    onError: (err) => toast.error(describeApiError(err)),
  });

  const linhasOk = rows.filter((r) => r.emissaoOK);
  const linhasResiduo = rows.filter((r) => !r.emissaoOK);
  const totalOk = linhasOk.reduce((s, r) => s + r.valor, 0);
  const totalResiduo = linhasResiduo.reduce((s, r) => s + r.valor, 0);

  const columns: GridColDef<PreviewAutomaticaLinha & { rowId: string }>[] = [
    {
      field: 'clienteNome',
      headerName: t('features.empresa.gerarNfEmpresa.preview.columns.cliente'),
      width: 220,
    },
    {
      field: 'clienteProjetoNome',
      headerName: t('features.empresa.gerarNfEmpresa.preview.columns.projeto'),
      width: 220,
    },
    {
      field: 'requisicaoCompraReferencia',
      headerName: t('features.empresa.gerarNfEmpresa.preview.columns.rc'),
      width: 180,
      valueGetter: (_, row) => row.requisicaoCompraReferencia ?? '—',
    },
    {
      field: 'valor',
      headerName: t('features.empresa.gerarNfEmpresa.preview.columns.valor'),
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number) => formatBRL(v),
    },
    {
      field: 'emissaoOK',
      headerName: t('features.empresa.gerarNfEmpresa.preview.columns.emissaoOk'),
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row.emissaoOK ? (
          <Chip
            size="small"
            color="success"
            icon={<CheckCircleIcon />}
            label={t('features.empresa.gerarNfEmpresa.preview.emissaoOkTrue')}
          />
        ) : (
          <Chip
            size="small"
            color="error"
            variant="outlined"
            icon={<ErrorOutlineIcon />}
            label={t('features.empresa.gerarNfEmpresa.preview.emissaoOkFalse')}
          />
        ),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {t('features.empresa.gerarNfEmpresa.preview.title', {
          colaborador: linhaSelecionada?.colaboradorNome ?? '',
        })}
      </DialogTitle>
      <DialogContent dividers>
        {previewQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : previewQuery.isError ? (
          <Alert severity="error">{describeApiError(previewQuery.error)}</Alert>
        ) : (
          <Stack spacing={2}>
            <Box>
              <DataGrid<PreviewAutomaticaLinha & { rowId: string }>
                autoHeight
                rows={rows}
                getRowId={(r) => r.rowId}
                columns={columns}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnMenu
                disableVirtualization
                hideFooter
              />
            </Box>

            <Divider />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('features.empresa.gerarNfEmpresa.preview.totalOkLabel', {
                    count: linhasOk.length,
                  })}
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatBRL(totalOk)}
                </Typography>
              </Box>
              {linhasResiduo.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('features.empresa.gerarNfEmpresa.preview.totalResiduoLabel', {
                      count: linhasResiduo.length,
                    })}
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {formatBRL(totalResiduo)}
                  </Typography>
                </Box>
              )}
            </Stack>

            {linhasResiduo.length > 0 && (
              <Alert severity="info" variant="outlined">
                {t('features.empresa.gerarNfEmpresa.preview.residuoHint')}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={gerarMut.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={gerarMut.isPending || previewQuery.isLoading || linhasOk.length === 0}
          onClick={() => gerarMut.mutate()}
        >
          {gerarMut.isPending
            ? t('common.saving')
            : t('features.empresa.gerarNfEmpresa.preview.confirmar', { count: linhasOk.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
